import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { humanizeKey } from "utils/humanizeKey";

/**
 * Renders an editable MUI form from a (Pydantic) JSON Schema. Supports the
 * shapes our configs use: object properties, string/number/integer/boolean,
 * enums (inline or via $ref into $defs), Optional (anyOf with null), nested
 * objects (recursive), arrays of strings (add-row editor), and arrays of
 * objects (editable tables, e.g. department->store / job-code maps). Anything
 * more exotic falls back to a raw-JSON text field so no config is ever
 * uneditable.
 *
 * Schema-driven UI contract (see integration_platform README):
 * - field/section labels come from schema titles, humanized;
 * - `description` renders as helper text for EVERY control type;
 * - `format: "multiline"` on a string renders a textarea (keys, PEM blocks);
 * - enums render an empty option ONLY when the field is optional and has no
 *   default — a required/defaulted enum can never be "—";
 * - `list[str]` renders as an add-row editor (never "one per line").
 *
 * Reused by connection config, step config, integration config, secret forms
 * and the wizard.
 */

type Schema = Record<string, any>;
type Json = Record<string, unknown>;

/**
 * Free-typing numeric field: plain text input (no number-input spinner
 * arrows), fully clearable, digits filtered as you type. Keeps a local
 * string draft so intermediate states ("", "-", "1.") do not fight the
 * parsed value; empty propagates ``undefined`` (=omit / None).
 */
function PlainNumberInput({
  label,
  placeholder,
  helper,
  required,
  disabled,
  isInteger,
  value,
  onChange,
}: {
  label?: string;
  placeholder?: string;
  helper?: string;
  required?: boolean;
  disabled?: boolean;
  isInteger: boolean;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
}) {
  const [draft, setDraft] = useState(value == null ? "" : String(value));

  // Sync external changes (form reset, prefill) without clobbering typing:
  // only overwrite the draft when it no longer parses to the current value.
  useEffect(() => {
    const parsed =
      draft.trim() === "" || draft === "-"
        ? undefined
        : isInteger
          ? parseInt(draft, 10)
          : Number(draft);
    const normalized = Number.isNaN(parsed as number) ? undefined : parsed;
    if (normalized !== value) {
      setDraft(value == null ? "" : String(value));
    }
  }, [value]);

  return (
    <TextField
      size="small"
      fullWidth
      inputMode={isInteger ? "numeric" : "decimal"}
      label={label}
      placeholder={placeholder}
      helperText={helper}
      required={required}
      disabled={disabled}
      value={draft}
      onChange={(e) => {
        const raw = e.target.value.replace(
          isInteger ? /[^0-9-]/g : /[^0-9.\-]/g,
          "",
        );
        setDraft(raw);
        if (raw.trim() === "" || raw === "-") {
          onChange(undefined);
          return;
        }
        const num = isInteger ? parseInt(raw, 10) : Number(raw);
        onChange(Number.isNaN(num) ? undefined : num);
      }}
    />
  );
}

interface Props {
  schema: Schema;
  value: Json;
  onChange: (value: Json) => void;
  disabled?: boolean;
}

function deref(node: Schema, root: Schema): Schema {
  if (node && typeof node.$ref === "string") {
    const path = node.$ref.replace(/^#\//, "").split("/");
    let resolved: any = root;
    for (const part of path) resolved = resolved?.[part];
    if (!resolved) return node;
    // Keep $ref siblings (Pydantic emits description/title/default NEXT TO
    // the $ref) — the property-level values must win over the $defs entry.
    const { $ref: _ref, ...siblings } = node;
    return { ...resolved, ...siblings };
  }
  return node;
}

/** Unwrap ``anyOf: [T, null]`` (Pydantic Optional) to its non-null branch. */
function unwrapOptional(node: Schema): Schema {
  if (Array.isArray(node.anyOf)) {
    const nonNull = node.anyOf.find((s: Schema) => s?.type !== "null");
    if (nonNull) {
      return {
        ...nonNull,
        title: node.title ?? nonNull.title,
        description: node.description ?? nonNull.description,
        default: node.default !== undefined ? node.default : nonNull.default,
      };
    }
  }
  return node;
}

function enumValues(node: Schema, root: Schema): string[] | null {
  const resolved = deref(node, root);
  if (Array.isArray(resolved.enum)) return resolved.enum.map(String);
  return null;
}

/** Add-row editor for ``list[str]`` fields (replaces "one per line"). */
function StringListEditor({
  label,
  helper,
  itemLabel,
  value,
  disabled,
  onChange,
}: {
  label: string;
  helper?: string;
  itemLabel?: string;
  value: string[];
  disabled?: boolean;
  onChange: (v: string[]) => void;
}) {
  const rows = Array.isArray(value) ? value : [];
  const setRow = (index: number, next: string) =>
    onChange(rows.map((row, i) => (i === index ? next : row)));
  const removeRow = (index: number) =>
    onChange(rows.filter((_, i) => i !== index));

  return (
    <Box>
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{ mb: helper ? 0.25 : 1 }}
      >
        {label}
      </Typography>
      {helper && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 1 }}
        >
          {helper}
        </Typography>
      )}
      <Stack spacing={1}>
        {rows.map((row, index) => (
          <Stack key={index} direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              fullWidth
              placeholder={itemLabel}
              disabled={disabled}
              value={row}
              onChange={(e) => setRow(index, e.target.value)}
            />
            {!disabled && (
              <IconButton
                size="small"
                aria-label="Remove value"
                onClick={() => removeRow(index)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        ))}
        {rows.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            No values yet.
          </Typography>
        )}
      </Stack>
      {!disabled && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onChange([...rows, ""])}
          sx={{ mt: 1 }}
        >
          Add
        </Button>
      )}
    </Box>
  );
}

function Field({
  name,
  schema,
  root,
  value,
  required,
  disabled,
  hideLabel,
  onChange,
}: {
  name: string;
  schema: Schema;
  root: Schema;
  value: unknown;
  required: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
  onChange: (v: unknown) => void;
}) {
  const node = unwrapOptional(deref(schema, root));
  const label = humanizeKey(String(node.title ?? name));
  const helper = hideLabel
    ? undefined
    : (node.description as string | undefined);
  const fieldLabel = hideLabel ? undefined : label;
  const defaultValue =
    schema.default !== undefined ? schema.default : node.default;

  const options = enumValues(node, root);
  if (options) {
    // An empty ("not set") choice exists only for a genuinely optional enum
    // with no default; required/defaulted enums always hold a real value.
    const allowEmpty = !required && defaultValue === undefined;
    const effective =
      value == null
        ? defaultValue != null
          ? String(defaultValue)
          : ""
        : String(value);
    return (
      <TextField
        select
        size="small"
        fullWidth
        label={fieldLabel}
        placeholder={hideLabel ? label : undefined}
        helperText={helper}
        required={required}
        disabled={disabled}
        value={effective}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        {allowEmpty && (
          <MenuItem value="">
            <em>Not set</em>
          </MenuItem>
        )}
        {options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            {humanizeKey(opt)}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (node.type === "boolean") {
    // Fall back to the schema default so an unset value renders the way
    // Pydantic will parse it (e.g. a row saved before the field existed).
    const checked = value == null ? Boolean(defaultValue) : Boolean(value);
    const control = (
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(_, next) => onChange(next)}
      />
    );
    if (hideLabel) return control;
    return (
      <Box>
        <FormControlLabel control={control} label={label} />
        {helper && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ ml: 6, mt: -0.5 }}
          >
            {helper}
          </Typography>
        )}
      </Box>
    );
  }

  // Like the boolean/enum branches above: an unset value renders as the
  // schema default, because that is what Pydantic will fill in at runtime.
  // The default is display-only - the key stays absent until edited.
  if (node.type === "integer" || node.type === "number") {
    const effective =
      typeof value === "number"
        ? value
        : typeof defaultValue === "number"
          ? defaultValue
          : undefined;
    return (
      <PlainNumberInput
        label={fieldLabel}
        placeholder={hideLabel ? label : undefined}
        helper={helper}
        required={required}
        disabled={disabled}
        isInteger={node.type === "integer"}
        value={effective}
        onChange={onChange}
      />
    );
  }

  if (node.type === "string") {
    // format: "multiline" is our schema-driven hint for textarea inputs
    // (SSH/PGP keys and similar block values).
    const multiline = node.format === "multiline";
    const effective =
      value ?? (defaultValue != null ? String(defaultValue) : "");
    return (
      <TextField
        size="small"
        fullWidth
        multiline={multiline}
        minRows={multiline ? 5 : undefined}
        label={fieldLabel}
        placeholder={hideLabel ? label : undefined}
        helperText={helper}
        required={required}
        disabled={disabled}
        value={effective}
        onChange={(e) => onChange(e.target.value || undefined)}
        InputProps={
          multiline
            ? { sx: { fontFamily: "monospace", fontSize: 13 } }
            : undefined
        }
      />
    );
  }

  if (node.type === "array") {
    const items = unwrapOptional(deref(node.items ?? {}, root));
    if (items.type === "string") {
      // Unset renders the schema default (what Pydantic applies at runtime);
      // an explicitly emptied list stays [] - a real "no values" override.
      const effective = Array.isArray(value)
        ? (value as string[])
        : Array.isArray(defaultValue)
          ? (defaultValue as string[])
          : [];
      return (
        <StringListEditor
          label={label}
          helper={helper}
          itemLabel={items.title ? humanizeKey(String(items.title)) : undefined}
          value={effective}
          disabled={disabled}
          onChange={onChange as (v: string[]) => void}
        />
      );
    }
    if (items.type === "object" && items.properties) {
      return (
        <ArrayOfObjects
          label={label}
          helper={node.description as string | undefined}
          itemSchema={items}
          root={root}
          value={Array.isArray(value) ? (value as Json[]) : []}
          disabled={disabled}
          onChange={onChange as (v: Json[]) => void}
        />
      );
    }
  }

  if (node.type === "object" && node.properties) {
    return (
      <Box
        sx={{
          p: 1.5,
          bgcolor: "grey.50",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1.5,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          {label}
        </Typography>
        {node.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mb: 1 }}
          >
            {node.description}
          </Typography>
        )}
        <Box sx={{ mt: node.description ? 0 : 1.5 }}>
          <ObjectFields
            schema={node}
            root={root}
            value={(value as Json) ?? {}}
            disabled={disabled}
            onChange={onChange as (v: Json) => void}
          />
        </Box>
      </Box>
    );
  }

  // Fallback: raw JSON so nothing is uneditable.
  return (
    <TextField
      size="small"
      fullWidth
      multiline
      minRows={2}
      label={`${label} (JSON)`}
      helperText={helper}
      disabled={disabled}
      value={value == null ? "" : JSON.stringify(value, null, 2)}
      onChange={(e) => {
        try {
          onChange(e.target.value ? JSON.parse(e.target.value) : undefined);
        } catch {
          /* keep typing until valid JSON */
        }
      }}
    />
  );
}

/** Editable table for a ``list[Model]`` (mapping tables). */
function ArrayOfObjects({
  label,
  helper,
  itemSchema,
  root,
  value,
  disabled,
  onChange,
}: {
  label: string;
  helper?: string;
  itemSchema: Schema;
  root: Schema;
  value: Json[];
  disabled?: boolean;
  onChange: (v: Json[]) => void;
}) {
  const properties: Record<string, Schema> = itemSchema.properties ?? {};
  const required: string[] = itemSchema.required ?? [];
  const columns = Object.keys(properties);
  const rows = Array.isArray(value) ? value : [];

  const setCell = (rowIndex: number, key: string, cellValue: unknown) => {
    const next = rows.map((row) => ({ ...row }));
    if (cellValue === undefined) delete next[rowIndex][key];
    else next[rowIndex][key] = cellValue;
    onChange(next);
  };
  // Seed new rows with schema defaults so what gets persisted matches what
  // Pydantic would fill in (and switches render in their default state).
  const addRow = () => {
    const seeded: Json = {};
    for (const [key, propSchema] of Object.entries(properties)) {
      const resolved = unwrapOptional(deref(propSchema, root));
      const dflt =
        propSchema.default !== undefined
          ? propSchema.default
          : resolved.default;
      if (dflt !== undefined && dflt !== null) seeded[key] = dflt;
    }
    onChange([...rows, seeded]);
  };
  const removeRow = (rowIndex: number) =>
    onChange(rows.filter((_, i) => i !== rowIndex));

  return (
    <Box>
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{ mb: helper ? 0.25 : 1 }}
      >
        {label}
      </Typography>
      {helper && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 1 }}
        >
          {helper}
        </Typography>
      )}
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 320 }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => {
                // Table cells render fields with hideLabel (no helper text),
                // so the row-model field descriptions surface here instead:
                // an info hint on the column header.
                const colSchema = unwrapOptional(deref(properties[col], root));
                const description = colSchema.description as
                  | string
                  | undefined;
                return (
                  <TableCell key={col}>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      component="span"
                    >
                      <span>
                        {humanizeKey(String(colSchema.title ?? col))}
                      </span>
                      {description && (
                        <Tooltip title={description} arrow placement="top">
                          <InfoOutlinedIcon
                            sx={{
                              fontSize: 15,
                              color: "text.secondary",
                              cursor: "help",
                            }}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                );
              })}
              {!disabled && <TableCell padding="checkbox" />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {columns.map((col) => (
                  <TableCell key={col} sx={{ verticalAlign: "top" }}>
                    <Field
                      name={col}
                      schema={properties[col]}
                      root={root}
                      value={row?.[col]}
                      required={required.includes(col)}
                      disabled={disabled}
                      hideLabel
                      onChange={(v) => setCell(rowIndex, col, v)}
                    />
                  </TableCell>
                ))}
                {!disabled && (
                  <TableCell padding="checkbox">
                    <IconButton
                      size="small"
                      aria-label="Remove row"
                      onClick={() => removeRow(rowIndex)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + 1}>
                  <Typography variant="caption" color="text.secondary">
                    No rows.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      {!disabled && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addRow}
          sx={{ mt: 1 }}
        >
          Add row
        </Button>
      )}
    </Box>
  );
}

function ObjectFields({
  schema,
  root,
  value,
  disabled,
  onChange,
}: {
  schema: Schema;
  root: Schema;
  value: Json;
  disabled?: boolean;
  onChange: (v: Json) => void;
}) {
  const properties: Record<string, Schema> = schema.properties ?? {};
  const required: string[] = schema.required ?? [];

  const setField = (key: string, fieldValue: unknown) => {
    const next = { ...value };
    if (fieldValue === undefined) delete next[key];
    else next[key] = fieldValue;
    onChange(next);
  };

  return (
    <Stack spacing={2}>
      {Object.entries(properties).map(([key, propSchema]) => (
        <Field
          key={key}
          name={key}
          schema={propSchema}
          root={root}
          value={value[key]}
          required={required.includes(key)}
          disabled={disabled}
          onChange={(v) => setField(key, v)}
        />
      ))}
    </Stack>
  );
}

export default function JsonSchemaForm({
  schema,
  value,
  onChange,
  disabled,
}: Props) {
  if (!schema || !schema.properties) {
    return (
      <Typography variant="body2" color="text.secondary">
        No configurable fields.
      </Typography>
    );
  }
  return (
    <ObjectFields
      schema={schema}
      root={schema}
      value={value ?? {}}
      disabled={disabled}
      onChange={onChange}
    />
  );
}
